import { calculateBlendedColor } from './ElementalColor';

// Gradient configuration constants
const GRADIENT_DIR = '180deg';
const GRADIENT_TOP_STOP = '5px';
const GRADIENT_BOTTOM_STOP = '5px';
const GRADIENT_INNER_ALPHA = 0.17;
const GRADIENT_OUTER_ALPHA = 0.0;
const GRADIENT_INNER_ALPHA_HOVER = 0.50;
const GRADIENT_OUTER_ALPHA_HOVER = 0.04;
/** My Builds active card: stronger top band than default (replaces primary inset border). */
const GRADIENT_INNER_ALPHA_ACTIVE = 0.72;
const GRADIENT_OUTER_ALPHA_ACTIVE = 0.06;
const GRADIENT_INNER_ALPHA_ACTIVE_HOVER = 0.72;
const GRADIENT_OUTER_ALPHA_ACTIVE_HOVER = 0.12;
const BORDER_ALPHA = 0.5;

/** Main card artwork & set thumbnails — tight core glow + soft outer halo. */
const ICON_GLOW_OFFSET_X_PX = 0;
const ICON_GLOW_OFFSET_Y_PX = 0;
const ICON_GLOW_BLUR_PX = 3;
const ICON_GLOW_ALPHA = 0.72;

const ICON_SHADOW_OFFSET_X_PX = 0;
const ICON_SHADOW_OFFSET_Y_PX = 0;
const ICON_SHADOW_BLUR_PX = 30;
const ICON_SHADOW_ALPHA = 0.5;

/** Compact tiles — same glow, dimmer: a dozen of them sit a thumb apart, so at
    full strength the haloes of neighbouring tiles read as one smear. */
const ICON_GLOW_ALPHA_COMPACT = 0.5;
const ICON_SHADOW_ALPHA_COMPACT = 0.3;

/** Small inline icons (e.g. legendary) — single drop-shadow. */
const ICON_SHADOW_SOFT_OFFSET_X_PX = 0;
const ICON_SHADOW_SOFT_OFFSET_Y_PX = 1;
const ICON_SHADOW_SOFT_BLUR_PX = 2;
const ICON_SHADOW_SOFT_ALPHA = 0.45;

const buildDropShadow = (r, g, b, offsetX, offsetY, blur, alpha) =>
    `drop-shadow(${offsetX}px ${offsetY}px ${blur}px rgba(${r}, ${g}, ${b}, ${alpha}))`;

const iconCoreGlow = (r, g, b) =>
    buildDropShadow(r, g, b, ICON_GLOW_OFFSET_X_PX, ICON_GLOW_OFFSET_Y_PX, ICON_GLOW_BLUR_PX, ICON_GLOW_ALPHA);

const topBandGradient = (r, g, b, innerAlpha, outerAlpha) =>
    `linear-gradient(${GRADIENT_DIR}, rgba(${r}, ${g}, ${b}, ${innerAlpha}) ${GRADIENT_TOP_STOP}, rgba(${r}, ${g}, ${b}, ${outerAlpha}) ${GRADIENT_BOTTOM_STOP})`;

export const buildElementGradient = (stats, compact = false) => {
    const { r, g, b } = calculateBlendedColor(stats || {});
    void compact;
    return topBandGradient(r, g, b, GRADIENT_INNER_ALPHA, GRADIENT_OUTER_ALPHA);
};

export const buildElementBorderColor = (stats) => {
    const { r, g, b } = calculateBlendedColor(stats || {});
    return `rgba(${r}, ${g}, ${b}, ${BORDER_ALPHA})`;
};

export const buildElementGradientHover = (stats, compact = false) => {
    const { r, g, b } = calculateBlendedColor(stats || {});
    void compact;
    return topBandGradient(r, g, b, GRADIENT_INNER_ALPHA_HOVER, GRADIENT_OUTER_ALPHA_HOVER);
};

export const buildElementGradientActive = (stats, compact = false) => {
    const { r, g, b } = calculateBlendedColor(stats || {});
    void compact;
    return topBandGradient(r, g, b, GRADIENT_INNER_ALPHA_ACTIVE, GRADIENT_OUTER_ALPHA_ACTIVE);
};

export const buildElementGradientActiveHover = (stats, compact = false) => {
    const { r, g, b } = calculateBlendedColor(stats || {});
    void compact;
    return topBandGradient(r, g, b, GRADIENT_INNER_ALPHA_ACTIVE_HOVER, GRADIENT_OUTER_ALPHA_ACTIVE_HOVER);
};

/** Tight core glow only (e.g. build slots) — same tint as cards, no wide halo. */
export const buildElementIconFilterCore = (stats) => {
    const { r, g, b } = calculateBlendedColor(stats || {});
    return iconCoreGlow(r, g, b);
};

/** Core + soft outer halo, at the given strengths. */
const iconFilter = (stats, glowAlpha, haloAlpha) => {
    const { r, g, b } = calculateBlendedColor(stats || {});
    const core = buildDropShadow(r, g, b, ICON_GLOW_OFFSET_X_PX, ICON_GLOW_OFFSET_Y_PX, ICON_GLOW_BLUR_PX, glowAlpha);
    const halo = buildDropShadow(r, g, b, ICON_SHADOW_OFFSET_X_PX, ICON_SHADOW_OFFSET_Y_PX, ICON_SHADOW_BLUR_PX, haloAlpha);
    return `${core} ${halo}`;
};

/** Drop-shadow filter for main card artwork (core + soft outer halo). */
export const buildElementIconFilter = (stats) =>
    iconFilter(stats, ICON_GLOW_ALPHA, ICON_SHADOW_ALPHA);

/** The same, dimmed for compact tiles. */
export const buildElementIconFilterCompact = (stats) =>
    iconFilter(stats, ICON_GLOW_ALPHA_COMPACT, ICON_SHADOW_ALPHA_COMPACT);

/** Smaller tinted shadow for inline icons (e.g. legendary). */
export const buildElementIconFilterSoft = (stats) => {
    const { r, g, b } = calculateBlendedColor(stats || {});
    return buildDropShadow(
        r,
        g,
        b,
        ICON_SHADOW_SOFT_OFFSET_X_PX,
        ICON_SHADOW_SOFT_OFFSET_Y_PX,
        ICON_SHADOW_SOFT_BLUR_PX,
        ICON_SHADOW_SOFT_ALPHA
    );
};
